import React, { useState } from 'react';
import { Box, Card, CardContent, Typography, TextField, Button, Grid, MenuItem, Alert, Snackbar, FormControlLabel, Checkbox } from '@mui/material';
import { contatoService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const motivos = [
  'Sugestão',
  'Crítica',
  'Erro no sistema',
  'Dúvida',
  'Outros'
];

const SuporteTecnico: React.FC = () => {
  const { user } = useAuth() as any;
  const navigate = useNavigate();
  const [form, setForm] = useState({
    nome: '',
    email: '',
    celular: '',
    motivo: motivos[0],
    mensagem: ''
  });
  const [sending, setSending] = useState(false);
  const [enviarCopia, setEnviarCopia] = useState(true);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' }>({ open: false, message: '', severity: 'success' });

  const handleChange = (field: string, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!form.nome.trim() || !form.email.trim() || !form.mensagem.trim()) {
      setSnackbar({ open: true, message: 'Por favor, preencha Nome, E-mail e Mensagem.', severity: 'warning' });
      return;
    }
    try {
      setSending(true);
      // Anexar motivo ao corpo da mensagem para compatibilidade com o backend existente
      const mensagemComMotivo = `Motivo: ${form.motivo}\n\n${form.mensagem}`;
      const resp = await contatoService.enviar({
        nome: form.nome.trim(),
        email: form.email.trim(),
        celular: form.celular.trim(),
        mensagem: mensagemComMotivo
      });
      if (resp?.data?.success) {
        // Enviar cópia para o usuário logado se habilitado e se tiver e-mail
        const emailUsuario = user?.email || user?.user_email || null;
        if (enviarCopia && emailUsuario) {
          try {
            await contatoService.enviar({
              nome: form.nome.trim() || 'Usuário',
              email: emailUsuario,
              celular: form.celular.trim(),
              mensagem: `Cópia da mensagem enviada ao suporte.\n\n${mensagemComMotivo}`
            });
          } catch (copyErr) {
            console.warn('Falha ao enviar cópia para o usuário logado:', copyErr);
          }
        }
        setSnackbar({ open: true, message: 'Mensagem enviada com sucesso! Em breve entraremos em contato.', severity: 'success' });
        setForm({ nome: '', email: '', celular: '', motivo: motivos[0], mensagem: '' });
      } else {
        setSnackbar({ open: true, message: resp?.data?.message || 'Não foi possível enviar sua mensagem agora. Tente novamente.', severity: 'error' });
      }
    } catch (err) {
      console.error('Erro ao enviar suporte:', err);
      setSnackbar({ open: true, message: 'Falha ao enviar mensagem. Verifique sua conexão e tente novamente.', severity: 'error' });
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Card>
        <CardContent>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            Suporte Técnico
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Envie sua mensagem para nosso suporte. Responderemos o mais breve possível.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nome"
                value={form.nome}
                onChange={(e) => handleChange('nome', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="E-mail"
                type="email"
                value={form.email}
                onChange={(e) => handleChange('email', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Celular"
                value={form.celular}
                onChange={(e) => handleChange('celular', e.target.value)}
                placeholder="(DDD) 9 9999-9999"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Motivo do contato"
                value={form.motivo}
                onChange={(e) => handleChange('motivo', e.target.value)}
              >
                {motivos.map(m => (
                  <MenuItem key={m} value={m}>{m}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Mensagem"
                multiline
                rows={4}
                value={form.mensagem}
                onChange={(e) => handleChange('mensagem', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <FormControlLabel
                  control={<Checkbox checked={enviarCopia} onChange={(e) => setEnviarCopia(e.target.checked)} />}
                  label="Enviar uma cópia para meu e-mail"
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="outlined" color="inherit" onClick={() => navigate('/app/dashboard')}>
                    Sair
                  </Button>
                  <Button variant="contained" onClick={handleSubmit} disabled={sending}>
                    {sending ? 'Enviando...' : 'Enviar'}
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
        <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SuporteTecnico;
